import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";
import { globalRemoveMaskReal } from "../../../shared/utils";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { ToastContent } from "../../../shared/components";
import { IVFormErros } from "../../../shared/forms";
import { getClientValidationSchema, IFormData } from "../components/createComponents/clientValidationSchema";

interface Props {
	id: string;
	formRef: any;
	issaveAndClose: () => boolean;
	setIsLoading: (val: boolean) => void;
	activeSignature: boolean;
	signContract: boolean;
	activeSetup: boolean;
	bonusActive: boolean;
	comissionActive: boolean;
	creditCard: boolean;
	tenantSolidarity: boolean;
}

export function useSaveClient({
	id,
	formRef,
	issaveAndClose,
	setIsLoading,
	activeSignature,
	signContract,
	activeSetup,
	bonusActive,
	comissionActive,
	creditCard,
	tenantSolidarity,
}: Props) {
	const toastHandler = useToast();
	const navigate = useNavigate();

	const handleSave = useCallback(
		async (dados: IFormData) => {
			try {
				setIsLoading(true);
				formRef.current?.setErrors({});

				dados.document = dados.document.replace(/[^a-zA-Z0-9\s]/g, "");
				dados.phone = dados.phone.replace(/[^0-9]/g, "");
				dados.mobilePhone = dados.mobilePhone ? dados.mobilePhone.replace(/[^0-9]/g, "") : "";
				dados.observationPartner = !dados.observationPartner ? "" : dados.observationPartner;
				delete dados.origem;

				dados.baseRateCalc = "aluguel";
				dados.activeSignature = activeSignature;
				dados.signContract = signContract ? "sign" : "not_sign";
				dados.tax_setup_enable = activeSetup;
				dados.bonusActive = bonusActive ? "active" : "inactive";
				dados.comissionActive = comissionActive ? "active" : "inactive";
				dados.creditCard = creditCard ? "active" : "inactive";
				dados.tax_setup_limit_default = dados.tax_setup_limit_default ? globalRemoveMaskReal(dados.tax_setup_limit_default) : "0";
				dados.tax_setup_limit_max = dados.tax_setup_limit_max ? globalRemoveMaskReal(dados.tax_setup_limit_max) : "0";
				dados.tax_setup_limit_min = dados.tax_setup_limit_min ? globalRemoveMaskReal(dados.tax_setup_limit_min) : "0";
				dados.tenantSolidarity = tenantSolidarity ? "active" : "inactive";
				dados.tenantSolidarityNum = dados.tenantSolidarityNum ? Number(dados.tenantSolidarityNum) : 0;

				const formValidationSchema = getClientValidationSchema(id === "novo");
				const dadosvalidados = await formValidationSchema.validate(dados, { abortEarly: false });

				if (id === "novo") {
					const result = await clientService.create(dadosvalidados);
					if (issaveAndClose()) navigate("/clientes");
					else navigate(`/clientes/detalhe/${result}`);
				} else {
					const result = await clientService.updateById(id, { id, ...dadosvalidados });

					if (result instanceof Error) throw result;

					if (issaveAndClose()) navigate("/clientes");

					toastHandler.present({
						type: "success",
						position: "top-right",
						messageOrContent: <ToastContent title="Sucesso" content="Cliente salvo com sucesso!" />,
					});
				}
			} catch (err: any) {
				if (err?.name === "ValidationError") {
					const validationErros: IVFormErros = {};
					err.inner.forEach((error: any) => {
						if (!error.path) return;
						validationErros[error.path] = error.message;
					});
					formRef.current?.setErrors(validationErros);
				}
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			} finally {
				setIsLoading(false);
			}
		},
		[
			id,
			formRef,
			issaveAndClose,
			setIsLoading,
			activeSignature,
			signContract,
			activeSetup,
			bonusActive,
			comissionActive,
			comissionActive,
			creditCard,
			tenantSolidarity,
			navigate,
			toastHandler,
		]
	);

	return { handleSave };
}
